// Acceso y registro unificados (A-03) · diseño de CaptAccion_Mockup_Premium.html (vista «Acceso»).
// /acceso abre la pestaña «Entrar» y /registro la pestaña «Crear cuenta».
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Constellation from "@/components/effects/Constellation";
import { Eye, EyeOff } from "lucide-react";
import "@/styles/landing-mockup.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STRENGTH_COLORS = ["#E26D5A", "#F0B35B", "#E0B970", "#7FD4A8"];
const STRENGTH_HINT = ["Usa 8+ caracteres con números y símbolos", "Débil", "Aceptable", "Buena", "Excelente"];

function strength(v) {
  let s = 0;
  if (v.length >= 8) s++;
  if (/\d/.test(v)) s++;
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) s++;
  if (/[^\w]/.test(v)) s++;
  return s;
}

const Err = ({ id, children }) => (children ? <p style={{ color: "#F1707A", fontSize: 12.5, marginTop: 2 }} data-testid={id}>{children}</p> : null);

export default function Auth({ tab = 0 }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(tab);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [lf, setLf] = useState({ email: "", password: "" });
  const [rf, setRf] = useState({ name: "", email: "", password: "" });
  const [accept, setAccept] = useState(false);
  const [params] = useSearchParams();
  const expired = params.get("sesion") === "caducada";
  // v22: llega desde la tarjeta Pro o Business de la landing. Sin pasarela de pago todavía, la cuenta empieza en Gratis.
  const wantedPlan = { pro: "Pro", business: "Business" }[params.get("plan") || ""] || "";

  const switchTab = (i) => {
    setT(i); setErrors({}); setFormError(""); setShowPw(false);
    navigate(i === 0 ? "/acceso" : "/registro", { replace: true });
  };
  const clearErr = (k) => { if (errors[k]) setErrors((e) => ({ ...e, [k]: "" })); };

  const submitLogin = async (e) => {
    e.preventDefault();
    setFormError("");
    const er = {};
    if (!lf.email.trim()) er.email = "Escribe tu correo.";
    else if (!EMAIL_RE.test(lf.email)) er.email = "Ese correo no tiene buena pinta.";
    if (!lf.password) er.password = "Escribe tu contraseña.";
    setErrors(er);
    if (Object.keys(er).length || submitting) return;
    setSubmitting(true);
    const res = await login(lf.email.trim().toLowerCase(), lf.password);
    setSubmitting(false);
    if (res.ok) navigate("/app/hoy"); else setFormError(res.error);
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setFormError("");
    const er = {};
    if (!rf.name.trim()) er.name = "Escribe tu nombre.";
    if (!EMAIL_RE.test(rf.email)) er.email = "Ese correo no tiene buena pinta.";
    if (rf.password.length < 8) er.password = "Al menos 8 caracteres.";
    if (!accept) er.accept = "Necesitamos que aceptes los términos y la política de privacidad.";
    setErrors(er);
    if (Object.keys(er).length || submitting) return;
    setSubmitting(true);
    const res = await register(rf.name.trim(), rf.email.trim().toLowerCase(), rf.password, accept,
      wantedPlan ? params.get("plan") : "");
    setSubmitting(false);
    if (res.ok) navigate("/app/hoy"); else setFormError(res.error);
  };

  const useDemo = async () => {
    if (demoLoading) return;
    setFormError(""); setDemoLoading(true);
    const res = await login("demo@captaccion.app", "captaccion2026");
    setDemoLoading(false);
    if (res.ok) navigate("/app/hoy"); else setFormError(res.error);
  };

  const s = strength(rf.password);
  const eyeBtn = (
    <button type="button" className="eye" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
      aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}>
      {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
    </button>
  );
  const errBox = formError && (
    <div data-testid={t === 0 ? "login-form-error" : "register-form-error"} style={{ borderRadius: 10, border: "1px solid rgba(241,112,122,.3)", background: "rgba(241,112,122,.08)", padding: "10px 14px", fontSize: 14, color: "#F1707A", marginBottom: 14 }}>{formError}</div>
  );

  return (
    <div className="lp">
      <div className="auth">
        <div className="auth-brand">
          <Constellation density={0.00009} />
          <Link to="/" aria-label="CaptAcción, portada">
            <img className="logo-img" src="/logo-captaccion-h96.webp" srcSet="/logo-captaccion-h96.webp 1x, /logo-captaccion-h192.webp 2x" alt="CaptAcción" />
          </Link>
          <div>
            <span className="eyebrow">Tu prospección, ordenada</span>
            <h2>A quién contactar, qué decir y <span className="serif text-metal">cuándo volver</span></h2>
            <p className="lead" style={{ fontSize: 16 }}>Tu primera agenda está a tres pasos:</p>
            <div className="onb">
              <div className="glass"><b>1</b><span>Crea tu cuenta<small>30 segundos</small></span></div>
              <div className="glass"><b>2</b><span>Cuéntanos tu negocio<small>Oferta, avatares y objetivos</small></span></div>
              <div className="glass"><b>3</b><span>Recibe tu primera agenda<small>A quién escribir y con qué mensaje</small></span></div>
            </div>
          </div>
          <small style={{ color: "var(--subtle)", fontSize: 12.5 }}>Solo leemos información pública · Nunca enviamos mensajes por ti</small>
        </div>

        <div className="auth-form">
          <div className="card-auth">
            {/* En móvil el panel de marca se oculta (como en el mockup): logo pequeño encima de las pestañas */}
            <Link to="/" className="auth-mlogo" aria-label="CaptAcción, portada">
              <img src="/logo-captaccion-h96.webp" srcSet="/logo-captaccion-h96.webp 1x, /logo-captaccion-h192.webp 2x" alt="CaptAcción" style={{ height: 28, width: "auto" }} />
            </Link>
            <div className="tabs" role="tablist" data-t={t} style={{ position: "relative" }}>
              <span className="ind"></span>
              <button type="button" role="tab" aria-selected={t === 0} onClick={() => switchTab(0)} data-testid="auth-tab-login">Entrar</button>
              <button type="button" role="tab" aria-selected={t === 1} onClick={() => switchTab(1)} data-testid="auth-tab-register">Crear cuenta</button>
            </div>

            {t === 0 ? (
              <form className="pane" onSubmit={submitLogin} noValidate>
                {expired && !formError && (
                  <div data-testid="login-session-expired" style={{ borderRadius: 10, border: "1px solid rgba(224,185,112,.35)", background: "rgba(224,185,112,.08)", padding: "10px 14px", fontSize: 14, color: "#E0B970", marginBottom: 14 }}>
                    Tu sesión ha caducado. Entra de nuevo para seguir donde lo dejaste.
                  </div>
                )}
                <h1>Hola de nuevo</h1>
                <p className="sub">Retoma justo donde lo dejaste.</p>
                <div className="field">
                  <label htmlFor="le">Correo</label>
                  <div className="inp"><input id="le" type="email" name="email" autoComplete="email" required placeholder="tucorreo@ejemplo.com" data-testid="login-email-input"
                    value={lf.email} onChange={(e) => { setLf({ ...lf, email: e.target.value }); clearErr("email"); }} /></div>
                  <Err id="login-email-error">{errors.email}</Err>
                </div>
                <div className="field">
                  <label htmlFor="lpw">Contraseña <Link to="/recuperar" data-testid="forgot-link">¿La olvidaste?</Link></label>
                  <div className="inp"><input id="lpw" name="password" type={showPw ? "text" : "password"} autoComplete="current-password" required placeholder="Tu contraseña" data-testid="login-password-input"
                    value={lf.password} onChange={(e) => { setLf({ ...lf, password: e.target.value }); clearErr("password"); }} />{eyeBtn}</div>
                  <Err id="login-password-error">{errors.password}</Err>
                </div>
                {errBox}
                <button type="submit" className="btn btn-gold btn-block" style={{ marginTop: 6 }} disabled={submitting} data-testid="login-submit-button">
                  {submitting ? "Entrando…" : <>Entrar <span className="arr">→</span></>}
                </button>
                <div className="or">O</div>
                <button type="button" className="demo-btn" onClick={useDemo} disabled={demoLoading} data-testid="login-demo-button">
                  <span className="ic">▶</span>
                  <span><b>{demoLoading ? "Preparando tu demo…" : "Probar la cuenta de demostración"}</b><small>Datos de ejemplo · sin registro</small></span>
                  {demoLoading ? <span className="spin go" aria-label="Cargando"></span> : <span className="go">→</span>}
                </button>
              </form>
            ) : (
              <form className="pane" onSubmit={submitRegister} noValidate>
                <h1>Crea tu cuenta</h1>
                <p className="sub">En un minuto tienes tu panel listo para empezar.</p>
                {wantedPlan && (
                  <div data-testid="register-plan-note" style={{ borderRadius: 10, border: "1px solid rgba(224,185,112,.35)", background: "rgba(224,185,112,.08)", padding: "10px 14px", fontSize: 13.5, lineHeight: 1.45, color: "#E0B970", marginBottom: 14 }}>
                    Has elegido {wantedPlan}. El pago con tarjeta llega muy pronto: de momento tu cuenta empieza en el plan Gratis y te avisamos cuando puedas pasarte a {wantedPlan}.
                  </div>
                )}
                <div className="field">
                  <label htmlFor="rn">Nombre</label>
                  <div className="inp"><input id="rn" name="name" autoComplete="name" required placeholder="Tu nombre" data-testid="register-name-input"
                    value={rf.name} onChange={(e) => { setRf({ ...rf, name: e.target.value }); clearErr("name"); }} /></div>
                  <Err id="register-name-error">{errors.name}</Err>
                </div>
                <div className="field">
                  <label htmlFor="re">Correo</label>
                  <div className="inp"><input id="re" type="email" name="email" autoComplete="email" required placeholder="tucorreo@ejemplo.com" data-testid="register-email-input"
                    value={rf.email} onChange={(e) => { setRf({ ...rf, email: e.target.value }); clearErr("email"); }} /></div>
                  <Err id="register-email-error">{errors.email}</Err>
                </div>
                <div className="field">
                  <label htmlFor="rp">Contraseña</label>
                  <div className="inp"><input id="rp" name="new-password" type={showPw ? "text" : "password"} autoComplete="new-password" minLength={8} required placeholder="Mínimo 8 caracteres" data-testid="register-password-input"
                    value={rf.password} onChange={(e) => { setRf({ ...rf, password: e.target.value }); clearErr("password"); }} />{eyeBtn}</div>
                  <div className="meter" aria-hidden="true">
                    {[0, 1, 2, 3].map((k) => <i key={k} style={{ background: k < s ? STRENGTH_COLORS[s - 1] : undefined }} />)}
                  </div>
                  <span className="hint">{STRENGTH_HINT[s]}</span>
                  <Err id="register-password-error">{errors.password}</Err>
                </div>
                <label className="check">
                  <input type="checkbox" checked={accept} data-testid="register-accept-terms"
                    onChange={(e) => { setAccept(e.target.checked); clearErr("accept"); }} />
                  <span>Acepto los <Link to="/terminos" target="_blank">términos</Link> y la <Link to="/privacidad" target="_blank">política de privacidad</Link>.</span>
                </label>
                <Err id="register-accept-error">{errors.accept}</Err>
                {errBox}
                <button type="submit" className="btn btn-gold btn-block" disabled={submitting} data-testid="register-submit-button">
                  {submitting ? "Creando…" : <>Crear cuenta <span className="arr">→</span></>}
                </button>
              </form>
            )}
            <Link to="/" className="back">← Volver a la portada</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
